import React, { useEffect, useState } from "react";
import Layout from "../../layout/Layout";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../common/loader";
import { fetchBookingDetails } from "../../redux/adminReducer";

function BookingDetail() {
  const { bookingId } = useParams();
  const [loading, setLoading] = useState(false);
  const { translations } = useSelector((state) => state.language);
  const dispatch = useDispatch();

  const bookingDetails = useSelector(
    (state) => state.admin.bookingDetails?.data || {}
  );

  useEffect(() => {
    setLoading(true);
    dispatch(fetchBookingDetails(bookingId)).then(() => {
      setLoading(false);
    });
  }, [dispatch, bookingId]);

  const BASE_URL = process.env.REACT_APP_BASE_URL || "https://jeema.co/api/v2/";

  const user = bookingDetails?.user_id || {};
  const salon = bookingDetails?.salon_id || {};
  const staff = bookingDetails?.staff_id || {};

  const userPic = user.profilePicture
    ? `${BASE_URL}${encodeURIComponent(user.profilePicture)}`
    : `${BASE_URL}brand/Image.jpg`;

  const salonPic = salon.business_logo
    ? `${BASE_URL}${encodeURIComponent(salon.business_logo)}`
    : `${BASE_URL}brand/Image.jpg`;

  const staffPic = staff.profile_picture
    ? `${BASE_URL}${encodeURIComponent(staff.profile_picture)}`
    : `${BASE_URL}brand/Image.jpg`;

  const statusMap = {
    200: "pending",
    201: "confirmed",
    202: "rescheduled",
    203: "cancelled",
    204: "completed",
    205: "no_show",
  };

  const statusColor = {
    pending: "bg-yellow-200 text-yellow-700",
    confirmed: "bg-blue-200 text-blue-700",
    rescheduled: "bg-purple-200 text-purple-700",
    cancelled: "bg-red-200 text-red-700",
    completed: "bg-green-200 text-green-700",
    no_show: "bg-gray-200 text-gray-700",
  };

  const statusKey = statusMap[bookingDetails?.status];

  return (
    <Layout>
      <div className="min-h-screen w-full px-4 py-4">
        <section className="pt-1 grid grid-cols-2 items-center">
          <h1 className="text-xl font-bold text-white">
            {translations.booking_details.bookingDetails}
            <small className="text-2xl font-semibold text-gray-600"> |</small>
            <small className="font-normal text-gray-600">
              {" "}
              {translations.booking_details.bookingDetailsManagement}
            </small>
          </h1>

          <div className="text-md text-white flex items-center justify-end space-x-1">
            <div className="rounded-full bg-[#5A189A] py-2 px-3">
              <Link to="/" className="text-white no-underline cursor-pointer">
                {translations.booking_details.dashboard}
              </Link>
              <span className="mx-1">/</span>
              <Link
                to="/booking"
                className="text-white no-underline cursor-pointer"
              >
                {translations.booking_details.bookings}
              </Link>
              <span className="mx-1">/</span>
              <span className="text-green-300">
                {translations.booking_details.bookingDetails}
              </span>
            </div>
          </div>
        </section>

        {loading ? (
          <Loader />
        ) : (
          <div className="grid grid-cols-1 gap-4 mt-4">
            {/* Row 1: User + Salon + Staff */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* User Details */}
              <div className="bg-gray-800 text-white p-4 rounded-xl shadow">
                <h2 className="text-lg font-semibold mb-4">
                  {translations.booking_details.userDetails}
                </h2>
                <div className="flex items-center gap-4">
                  <img
                    src={userPic}
                    alt="User"
                    className="h-25 w-25 object-cover rounded-lg"
                  />
                  <div>
                    <p>
                      <span className="font-semibold text-gray-400">
                        {translations.booking_details.name} :
                      </span>{" "}
                      {user.name}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-400">
                        {translations.booking_details.email} :
                      </span>{" "}
                      {user.email
                        ? user.email
                        : translations.booking_details.notAvailable}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-400">
                        {translations.booking_details.gender} :
                      </span>{" "}
                      {user.gender === 0
                        ? translations.booking_details.male
                        : user.gender === 1
                        ? translations.booking_details.female
                        : translations.booking_details.other}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-400">
                        {translations.booking_details.phone} :
                      </span>{" "}
                      {user.fullPhoneNumber}
                    </p>
                  </div>
                </div>
              </div>

              {/* Salon Details */}
              <div className="bg-gray-800 text-white p-4 rounded-xl shadow">
                <h2 className="text-lg font-semibold mb-4">
                  {translations.booking_details.salonDetails}
                </h2>
                <div className="flex items-center gap-4">
                  <img
                    src={salonPic}
                    alt="Salon"
                    className="h-25 w-25 object-cover rounded-lg"
                  />
                  <div>
                    <p>
                      <span className="font-semibold text-gray-400">
                        {translations.booking_details.name} :
                      </span>{" "}
                      {salon.business_name}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-400">
                        {translations.booking_details.email} :
                      </span>{" "}
                      {salon.email}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-400">
                        {translations.booking_details.phone} :
                      </span>{" "}
                      {salon.business_phone}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-400">
                        {translations.booking_details.commercialId} :
                      </span>{" "}
                      {salon.commercial_id}
                    </p>
                  </div>
                </div>
              </div>

              {/* Staff Details */}
              <div className="bg-gray-800 text-white p-4 rounded-xl shadow">
                <h2 className="text-lg font-semibold mb-4">
                  {translations.booking_details.staffDetails}
                </h2>
                <div className="flex items-center gap-4">
                  <img
                    src={staffPic}
                    alt="Staff"
                    className="h-25 w-25 object-cover rounded-lg"
                  />
                  <div>
                    <p>
                      <span className="font-semibold text-gray-400">
                        {translations.booking_details.name} :
                      </span>{" "}
                      {staff.name}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-400">
                        {translations.booking_details.phone} :
                      </span>{" "}
                      {staff.phone}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-400">
                        {translations.booking_details.gender} :
                      </span>{" "}
                      {staff.gender === 0
                        ? translations.booking_details.male
                        : translations.booking_details.female}
                    </p>
                    <p>
                      <span className="font-semibold text-gray-400">
                        {translations.booking_details.genderService} :
                      </span>{" "}
                      {staff.gender_services?.join(", ")}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Row 2: Payment + Booking Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Payment Details */}
              <div className="bg-gray-800 text-white p-4 rounded-xl shadow">
                <h2 className="text-lg font-semibold mb-4">
                  {translations.booking_details.paymentDetails}
                </h2>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-400">
                      {translations.booking_details.paymentType} :
                    </span>
                    <span>
                      {bookingDetails.payment_method === 21
                        ? "Cash"
                        : bookingDetails.payment_method === 22
                        ? "Card"
                        : "Wallet"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-400">
                      {translations.booking_details.paymentStatus} :
                    </span>
                    <span>{bookingDetails.payment_status === 11 ? "Paid" : "Unpaid"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-400">
                      {translations.booking_details.promoCode} :
                    </span>
                    <span>{bookingDetails?.coupon_id?.code || translations.booking_details.notAvailable}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-400">
                      {translations.booking_details.discountType} :
                    </span>
                    <span>{bookingDetails?.coupon_id?.discountType || translations.booking_details.notAvailable}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-400">
                      {translations.booking_details.totalAmount} :
                    </span>
                    <span>
                      {bookingDetails.currency} {bookingDetails.total_amount}
                    </span>
                  </div>
                </div>
              </div>

              {/* Booking Details */}
              <div className="bg-gray-800 text-white p-4 rounded-xl shadow">
                <h2 className="text-lg font-semibold mb-4">
                  {translations.booking_details.bookingDetails}
                </h2>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-400">
                      {translations.booking_details.date} :
                    </span>
                    <span>{bookingDetails.booking_date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-400">
                      {translations.booking_details.time} :
                    </span>
                    <span>
                      {bookingDetails.start_time} - {bookingDetails.end_time}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-400">
                      {translations.booking_details.duration} :
                    </span>
                    <span>{bookingDetails.total_duration} mins</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-400">
                      {translations.booking_details.status} :
                    </span>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        statusColor[statusKey] || "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {statusKey
                        ? statusKey.replace("_", " ").toUpperCase()
                        : "UNKNOWN"}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="font-semibold text-gray-400">
                      {translations.booking_details.categories} :
                    </span>
                    <span>
                      {
                        bookingDetails.multiple_category?.[0].service_id?.service_name
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default BookingDetail;
